import React, { useState, useEffect } from 'react';
import VolumePricingManager from './VolumePricingManager';

const ConsignmentProductManager = ({ farmerId, farmerName, products: initialProducts, onUpdate, onClose }) => {
  const [products, setProducts] = useState(initialProducts || []);
  const [loading, setLoading] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  if (!initialProducts || initialProducts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No products found for this farmer.
      </div>
    );
  }

  const categories = ['all', ...new Set(products.map(p => p.category || 'Uncategorized'))];

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleProduct = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const handleUpdateCommission = async (productId, field, value) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const body = { 
        farmerId: farmerId, 
        productId: productId,
        [field]: value === '' ? null : parseFloat(value)
      };
      
      const res = await fetch('/api/hub/update-commission', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      
      if (!res.ok) throw new Error(await res.text());
      
      const fresh = await fetch(`/api/hub/farmer-products/${farmerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await fresh.json();
      setProducts(data);
      if (onUpdate) onUpdate();
      
    } catch (err) {
      console.error('Failed to update:', err);
      alert('Error updating: ' + err.message);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-700">{farmerName}</span>
          <span className="text-sm text-gray-400">({products.length} products)</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border rounded px-3 py-1 text-sm w-40"
          />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="border rounded px-3 py-1 text-sm"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat === 'all' ? 'All Categories' : cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {filteredProducts.map((product, index) => {
          const productId = product.productId;
          if (!productId) {
            console.warn('Product missing productId:', product);
            return null;
          }
          const isExpanded = expandedProduct === productId;
          
          return (
            <div key={productId} className="border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleProduct(productId)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-lg flex-shrink-0">{isExpanded ? '▼' : '▶'}</span>
                  <div className="min-w-0">
                    <span className="font-medium">{product.name}</span>
                    <span className="text-sm text-gray-500 ml-2">
                      ${product.price}/{product.unit}
                    </span>
                    {product.organic && (
                      <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                        Organic
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-gray-400">
                    {product.inventory > 0 ? `${product.inventory} in stock` : 'Out of stock'}
                  </span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                    {product.currentCommission || product.defaultCommission || 0}%
                  </span>
                </div>
              </button>

              {isExpanded && (
                <div className="p-4 bg-white border-t">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Category:</span> {product.category || 'Uncategorized'}
                      </p>
                      {product.description && (
                        <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Base Price:</span> ${product.price}/{product.unit}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Stock:</span> {product.inventory}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-gray-600">
                          <span className="font-medium">Commission:</span>
                        </span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          defaultValue={product.currentCommission || product.defaultCommission || 0}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val >= 0 && val <= 100) {
                              handleUpdateCommission(productId, 'commissionRate', val);
                            }
                          }}
                          className="w-16 border rounded px-2 py-0.5 text-sm"
                          disabled={updating}
                        />
                        <span className="text-sm text-gray-400">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Volume Pricing Tiers</p>
                    <VolumePricingManager
                      productId={productId}
                      productName={product.name}
                      onUpdate={() => {
                        const token = localStorage.getItem('token');
                        fetch(`/api/hub/farmer-products/${farmerId}`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        })
                          .then(res => res.json())
                          .then(data => {
                            setProducts(data);
                            if (onUpdate) onUpdate();
                          })
                          .catch(err => console.error('Failed to refresh:', err));
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 text-sm text-gray-400">
        Showing {filteredProducts.length} of {products.length} products
        {searchTerm && ` (filtered by "${searchTerm}")`}
      </div>
    </div>
  );
};

export default ConsignmentProductManager;
